'use client';

import React, { useState, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { HiOutlineMail } from '@react-icons/all-files/hi/HiOutlineMail';
import { HiOutlineTrendingUp } from '@react-icons/all-files/hi/HiOutlineTrendingUp';
import { HiOutlineLightBulb } from '@react-icons/all-files/hi/HiOutlineLightBulb';
import {
  StyledToolHero,
  StyledToolBody,
  StyledCalculator,
  StyledFieldsGrid,
  StyledFormGroup,
  StyledFullWidth,
  StyledCalculateButton,
  StyledResult,
  StyledCTACategory,
  StyledCTAOption,
  StyledWidgetsSection,
  StyledWidgetGrid,
  StyledWidgetCard,
  StyledRelatedTools,
  StyledRelatedToolGrid,
  StyledRelatedToolCard,
} from './CTACopyTool.styles';

interface IWidget {
  _id: string;
  name: string;
  slug: string;
  logo: string;
  teaser: string;
}

interface IRelatedTool {
  slug: string;
  name: string;
  description: string;
  icon?: string;
}

interface ICTAResult {
  buttons: string[];
  headlines: string[];
  emailSubjects: string[];
  adCopy: string[];
  productName: string;
  status: 'poor' | 'fair' | 'good';
}

interface IProps {
  recommendedWidgets: IWidget[];
  relatedTools: IRelatedTool[];
}

type TAction = 'Sign Up' | 'Buy Now' | 'Learn More' | 'Download' | 'Start Free Trial' | 'Book Demo' | 'Subscribe' | 'Get Quote';
type TTone = 'Professional' | 'Casual' | 'Urgent' | 'Playful' | 'Bold';

const CTA_TEMPLATES: Record<TAction, Record<TTone, { buttons: string[]; headlines: string[]; emailSubjects: string[]; adCopy: string[] }>> = {
  'Sign Up': {
    Professional: {
      buttons: ['Sign Up for {product}', 'Create Your {product} Account', 'Get Started with {product}', 'Join {product} Today', 'Start with {product}'],
      headlines: ['{value} - Sign Up for {product} Today', 'Join Thousands Who Trust {product} for {value}', 'Create Your Free {product} Account and {value}'],
      emailSubjects: ['Your {product} account is waiting', 'Ready to {value}? Sign up for {product}', '{product}: Start achieving {value} today'],
      adCopy: ['Sign up for {product} and {value}. Trusted by businesses in {industry}. No credit card required.', 'Join {product} today. {value} starts with one click. Free to get started.'],
    },
    Casual: {
      buttons: ['Jump In Free', 'Count Me In', 'Let\'s Go - Sign Up', 'Yep, Sign Me Up', 'Start for Free'],
      headlines: ['{value}? {product} Makes It Easy', 'Join {product} and Start Seeing Results', 'Your Shortcut to {value} Starts Here'],
      emailSubjects: ['Hey, {product} is ready for you', 'You + {product} = {value}', 'Quick sign-up, big results with {product}'],
      adCopy: ['{product} makes {value} easy. Sign up in seconds, no strings attached. Try it free.', 'Tired of overcomplicating things? {product} keeps it simple. Sign up free today.'],
    },
    Urgent: {
      buttons: ['Sign Up Now - Free', 'Claim Your Spot', 'Join {product} Before It\'s Full', 'Don\'t Wait - Sign Up', 'Get Access Now'],
      headlines: ['Limited Spots: Sign Up for {product} Before They Are Gone', '{value} Is Slipping Away. {product} Can Help.', 'Act Now: Join {product} and {value} Today'],
      emailSubjects: ['Last chance to join {product}', 'Spots filling fast for {product}', 'Don\'t miss out on {value} with {product}'],
      adCopy: ['Time is running out. Sign up for {product} now and {value} before it is too late. Free access, limited time.', '{product} spots are filling fast. Join now to {value}. No credit card needed.'],
    },
    Playful: {
      buttons: ['Let\'s Do This!', 'Sign Me Up Already', 'I\'m In!', 'Start the Fun', 'Join the Party'],
      headlines: ['Welcome to {product} - Where {value} Gets Fun', '{product} + You = {value} (It\'s a Match)', 'Ready to {value}? {product} Has Your Back'],
      emailSubjects: ['Guess what? {product} is ready for you', '{product} says hi (and {value})', 'Your {product} adventure starts now'],
      adCopy: ['Meet {product}, your new favorite way to {value}. Sign up free, zero hassle, all the good stuff.', '{value} should not be boring. {product} makes it fun. Jump in free today.'],
    },
    Bold: {
      buttons: ['Join {product} Now', 'Get {product} Free', 'Start Winning with {product}', 'Take Control - Sign Up', 'Unlock {product}'],
      headlines: ['Stop Settling. Start Using {product} to {value}.', '{product}: Built for {industry} Leaders Who Demand {value}', 'Own Your {value}. Start with {product}.'],
      emailSubjects: ['You deserve {value}. {product} delivers.', 'Stop wasting time. Start {product}.', '{product}: Your unfair advantage for {value}'],
      adCopy: ['{product} is the {industry} tool that delivers {value}. No fluff, no filler. Sign up free and see for yourself.', 'Leaders in {industry} use {product} to {value}. Are you ready to join them? Start free.'],
    },
  },
  'Buy Now': {
    Professional: {
      buttons: ['Buy {product} Now', 'Purchase {product}', 'Order {product} Today', 'Get {product}', 'Invest in {product}'],
      headlines: ['{value} Starts When You Buy {product}', 'Invest in {product} and {value} Immediately', 'Get {product} Today and Experience {value}'],
      emailSubjects: ['{product} is ready for purchase', 'Get {product} and {value} today', 'Your {product} order is one click away'],
      adCopy: ['Buy {product} and {value}. Trusted by {industry} professionals worldwide. Order now.', '{product} delivers {value} from day one. Buy now and see the difference.'],
    },
    Casual: {
      buttons: ['Grab It Now', 'Get Yours', 'Treat Yourself', 'Snag {product}', 'Go For It'],
      headlines: ['Why Wait? Grab {product} and {value}', '{product} Is Calling Your Name', 'Get {product} Today - You Deserve {value}'],
      emailSubjects: ['{product} is flying off the shelves', 'You are going to love {product}', 'Grab {product} before everyone else'],
      adCopy: ['{product} is the easiest way to {value}. Grab yours today and see why everyone is talking about it.', 'Ready to {value}? {product} makes it happen. Get yours now.'],
    },
    Urgent: {
      buttons: ['Buy Now - Limited Stock', 'Get {product} Before Price Goes Up', 'Order Now - Selling Fast', 'Buy Today, Save Big', 'Last Chance to Buy'],
      headlines: ['Price Increase Coming: Buy {product} Now', '{product} Is Selling Fast. Do Not Miss Out on {value}.', 'Final Hours: Get {product} at This Price'],
      emailSubjects: ['Price going up tomorrow for {product}', 'Last chance to get {product} at this price', 'Hurry: {product} is almost sold out'],
      adCopy: ['{product} is selling fast. Buy now before the price increases and {value} before it is too late.', 'Only a few left. Get {product} today and {value}. This deal will not last.'],
    },
    Playful: {
      buttons: ['Yes, I Want It!', 'Take My Money', 'Gimme {product}', 'Make It Mine', 'Add to Cart - Obviously'],
      headlines: ['You + {product} = {value} (No Brainer)', '{product} Is the Best Decision You Will Make Today', 'Go On. Treat Yourself to {product}.'],
      emailSubjects: ['You know you want {product}', '{product} + you = a very good decision', 'Your cart misses {product}'],
      adCopy: ['You deserve nice things. {product} is one of them. Buy now and enjoy {value}.', '{product} is basically a no-brainer for {value}. Get it. You will thank yourself later.'],
    },
    Bold: {
      buttons: ['Buy {product} - No Excuses', 'Get {product} Now', 'Own {product} Today', 'Invest in {value}', 'Make the Move'],
      headlines: ['Stop Thinking About It. Buy {product} and {value}.', '{product}: The {industry} Investment That Pays for Itself', 'Take Action. Buy {product}. Get {value}.'],
      emailSubjects: ['Stop researching. Start buying {product}.', '{product} pays for itself. Here is how.', 'The {industry} tool you should have bought yesterday'],
      adCopy: ['Every day without {product} is a day without {value}. Buy now and take control of your {industry} results.', '{product} is not cheap. It is an investment in {value}. And it pays for itself fast.'],
    },
  },
  'Learn More': {
    Professional: {
      buttons: ['Learn More About {product}', 'Discover {product}', 'Explore {product}', 'See How {product} Works', 'Request More Info'],
      headlines: ['Discover How {product} Delivers {value}', 'See Why {industry} Leaders Choose {product}', 'Learn How {product} Can Help You {value}'],
      emailSubjects: ['Discover what {product} can do for you', 'How {product} helps {industry} businesses {value}', 'See {product} in action'],
      adCopy: ['Curious about {product}? Learn how it helps {industry} businesses {value}. No commitment required.', 'Discover why {product} is the go-to solution for {value} in {industry}. Learn more today.'],
    },
    Casual: {
      buttons: ['Tell Me More', 'What\'s {product}?', 'Show Me How', 'Curious? Click Here', 'Check It Out'],
      headlines: ['What Is {product} and Why Should You Care?', '{value} Made Simple with {product}', 'Here Is How {product} Actually Works'],
      emailSubjects: ['So, what is {product} anyway?', 'Curious about {value}? Let us explain.', '{product} in 2 minutes'],
      adCopy: ['Wondering how {product} can help you {value}? Here is the short version. Click to learn more.', '{product} does one thing really well: {value}. Want to know how? Check it out.'],
    },
    Urgent: {
      buttons: ['Learn More Now', 'See Why - Before It Is Too Late', 'Find Out Today', 'Discover Now', 'Do Not Miss This'],
      headlines: ['Your Competitors Already Know About {product}. Do You?', 'Every Day Without {product} Is Costing You {value}', 'Learn About {product} Now, Before You Fall Behind'],
      emailSubjects: ['Your competitors are using {product}. Are you?', 'Find out what you are missing with {product}', 'Time-sensitive: learn about {product} now'],
      adCopy: ['Your competitors in {industry} are already using {product} to {value}. Find out what you are missing.', 'Do not wait to learn about {product}. The {industry} landscape is changing fast.'],
    },
    Playful: {
      buttons: ['Ooh, Tell Me More', 'I\'m Curious', 'Spill the Details', 'Show Me the Magic', 'What\'s the Deal?'],
      headlines: ['{product}: The Thing You Did Not Know You Needed', 'Spoiler: {product} Makes {value} Way Easier', 'Curious? {product} Is Worth a Look.'],
      emailSubjects: ['Psst... have you heard of {product}?', '{product} just might blow your mind', 'The {value} secret everyone is talking about'],
      adCopy: ['Okay, we will spill. {product} helps you {value} without the usual headaches. Curious? Click to learn more.', '{product} is the {industry} secret that is not so secret anymore. Come see what the buzz is about.'],
    },
    Bold: {
      buttons: ['See What {product} Can Do', 'Learn the Truth About {value}', 'Discover {product}\'s Power', 'See Results Now', 'Explore {product}'],
      headlines: ['What {product} Does That Nothing Else Can', '{value} Is Not Optional. {product} Makes It Possible.', 'The {industry} Tool That Changes Everything. Learn More.'],
      emailSubjects: ['{product}: the tool your {industry} needs', 'This changes everything for {value}', 'Bold move: learn about {product}'],
      adCopy: ['{product} is not just another {industry} tool. It is the one that actually delivers {value}. See for yourself.', 'Forget everything you know about {value}. {product} rewrites the rules. Learn more.'],
    },
  },
  'Download': {
    Professional: {
      buttons: ['Download {product}', 'Get Your Free Download', 'Download Now', 'Access {product}', 'Get the {product}'],
      headlines: ['Download {product} and Start Getting {value}', 'Access {product} Instantly - Free Download', 'Your Free {product} Download Is Ready'],
      emailSubjects: ['Your {product} download is ready', 'Download {product} and {value} today', 'Free download: {product} for {industry}'],
      adCopy: ['Download {product} free and {value}. Used by {industry} professionals worldwide.', 'Get instant access to {product}. Download now and start seeing {value}.'],
    },
    Casual: {
      buttons: ['Grab the Download', 'Get It Free', 'Download - It\'s Free', 'Snag Your Copy', 'Yes, Download It'],
      headlines: ['Free Download: {product} for Instant {value}', 'Download {product} - No Strings Attached', 'Grab {product} Free and {value} Today'],
      emailSubjects: ['Free {product} download inside', 'Grab your {product} copy', 'Download this and {value}'],
      adCopy: ['{product} is free to download and helps you {value}. Grab your copy right now.', 'Want to {value}? Download {product} free. Takes 10 seconds.'],
    },
    Urgent: {
      buttons: ['Download Before It Is Gone', 'Get It Now - Free', 'Limited Time Download', 'Download Today', 'Instant Access'],
      headlines: ['Free Download Available Now: {product}', 'Limited Time: Download {product} and {value}', 'Act Fast: Get {product} Before the Link Expires'],
      emailSubjects: ['Download {product} now - link expires soon', 'Last chance: free {product} download', 'Hurry: {product} download is live'],
      adCopy: ['Download {product} now while it is still free. {value} for {industry} professionals. Do not wait.', 'This free {product} download will not last forever. Get it now and start seeing {value}.'],
    },
    Playful: {
      buttons: ['Yep, Download It', 'Grab It!', 'Free Stuff Inside', 'Download the Good Stuff', 'Get It, It\'s Free'],
      headlines: ['Free Download Alert: {product} Is Here', '{product} + Download Button = {value}', 'Your New Favorite Download: {product}'],
      emailSubjects: ['Free stuff alert: download {product}', 'Click, download, {value}. That simple.', '{product} just dropped. Grab it free.'],
      adCopy: ['Free downloads that actually deliver? Meet {product}. Download it and {value} in minutes.', '{product} is free, useful, and ready to download. What are you waiting for?'],
    },
    Bold: {
      buttons: ['Download {product} Free', 'Get Instant Access', 'Own {product} Now', 'Download and Dominate', 'Get the Edge'],
      headlines: ['Download {product}: The {industry} Advantage', 'Stop Guessing. Download {product} and {value}.', 'Your Competitors Downloaded {product}. Your Turn.'],
      emailSubjects: ['Download {product} and own {value}', 'The {industry} download everyone is talking about', 'Get ahead: download {product} now'],
      adCopy: ['{product} is the download that gives {industry} professionals the edge. Get it free and {value}.', 'Stop falling behind. Download {product} and take control of {value}. Free, instant access.'],
    },
  },
  'Start Free Trial': {
    Professional: {
      buttons: ['Start Your Free Trial', 'Try {product} Free', 'Begin Free Trial', 'Activate Free Trial', 'Start Free - No Card Needed'],
      headlines: ['Try {product} Free and See {value} for Yourself', 'Start Your Free {product} Trial Today', 'Experience {product} Risk-Free for 14 Days'],
      emailSubjects: ['Your free {product} trial is waiting', 'Try {product} free - no credit card needed', 'Start your {product} trial today'],
      adCopy: ['Try {product} free for 14 days. No credit card, no commitment. See how it delivers {value} for {industry}.', 'Start your free {product} trial and {value} from day one. Cancel anytime.'],
    },
    Casual: {
      buttons: ['Try It Free', 'Take It for a Spin', 'Test Drive {product}', 'Free Trial - Let\'s Go', 'Give It a Shot'],
      headlines: ['Take {product} for a Free Spin', 'Free Trial: See Why Everyone Loves {product}', 'No Risk, No Card. Just Try {product} Free.'],
      emailSubjects: ['Take {product} for a spin', 'Free trial, no strings. Try {product}.', 'Your free {product} test drive awaits'],
      adCopy: ['Curious about {product}? Take it for a free spin and see {value} without spending a cent.', '{product} free trial: no card, no commitment, no catch. Just {value}.'],
    },
    Urgent: {
      buttons: ['Start Your Free {product} Trial Before Price Increases', 'Try {product} Free - Limited Time', 'Act Now - Free Trial', 'Free Trial Ending Soon', 'Last Chance - Try Free'],
      headlines: ['Start Your Free {product} Trial Before This Offer Expires', 'Free Trial Spots Are Running Out for {product}', 'Try {product} Free Now - This Will Not Last'],
      emailSubjects: ['Free trial ending soon for {product}', 'Last chance to try {product} free', 'Hurry: {product} free trial, limited time'],
      adCopy: ['Try {product} free while the offer lasts. {value} is one click away. Do not wait.', 'Free trial spots for {product} are limited. Start today and {value} before time runs out.'],
    },
    Playful: {
      buttons: ['Heck Yes, Try Free', 'Free Trial? Sold.', 'Let\'s Try It!', 'I\'m In - Start Free', 'Free Trial Sounds Great'],
      headlines: ['Free Trial: {product} Is About to Become Your Favorite', '{product} Free Trial - All Gain, No Pain', 'Start Free and Watch {product} Work Its Magic'],
      emailSubjects: ['Free trial alert: {product} is waiting', 'Try {product} free - you will love it', 'Your {product} free trial adventure begins'],
      adCopy: ['{product} free trial: no card, no stress, just {value}. Come on in, the water is fine.', 'Try {product} free and see why {industry} teams cannot stop talking about it.'],
    },
    Bold: {
      buttons: ['Start Free Trial Now', 'Try {product} - Prove It Yourself', 'Launch Your Free Trial', 'Get Started Free', 'Activate Your Trial'],
      headlines: ['Prove It to Yourself: Try {product} Free', '{product}: 14 Days to See {value}. Zero Risk.', 'Start Free. See Results. Stay Because {product} Delivers.'],
      emailSubjects: ['Prove {product} delivers. Try it free.', '14 days to {value}. Start your {product} trial.', '{product} free trial: results or nothing'],
      adCopy: ['{product} does not need a sales pitch. Start your free trial and let the results speak. {value} guaranteed.', 'Bold claim: {product} will change how you {value}. Start a free trial and see.'],
    },
  },
  'Book Demo': {
    Professional: {
      buttons: ['Book a Demo', 'Schedule Your Demo', 'Request a Demo', 'See {product} in Action', 'Get a Personalized Demo'],
      headlines: ['See How {product} Delivers {value} - Book Your Demo', 'Schedule a Personalized {product} Demo Today', 'Watch {product} in Action - Request Your Demo'],
      emailSubjects: ['Book your {product} demo today', 'See {product} in action - schedule a demo', 'Your personalized {product} demo awaits'],
      adCopy: ['Book a free {product} demo and see how it delivers {value} for {industry} businesses. No obligation.', 'Schedule a personalized demo of {product}. See exactly how it can help you {value}.'],
    },
    Casual: {
      buttons: ['Let\'s Chat', 'Show Me a Demo', 'Book a Quick Demo', 'See It in Action', 'Walk Me Through It'],
      headlines: ['Curious About {product}? Let Us Show You', 'Book a Quick {product} Demo - No Pressure', 'See {product} Live - It Takes 15 Minutes'],
      emailSubjects: ['Quick {product} demo? Just 15 minutes.', 'Let us show you what {product} can do', 'Curious? Book a {product} demo.'],
      adCopy: ['Want to see {product} in action? Book a quick demo. 15 minutes, no pressure, no slides.', 'Book a {product} demo and see {value} live. It only takes a few minutes.'],
    },
    Urgent: {
      buttons: ['Book Your Demo Now', 'Demo Slots Filling Up', 'Schedule Before Spots Run Out', 'Book Today - Limited Availability', 'Reserve Your Demo'],
      headlines: ['Demo Slots for {product} Are Filling Up Fast', 'Book Your {product} Demo Before Availability Runs Out', 'Do Not Wait - See {product} Deliver {value} Now'],
      emailSubjects: ['Demo slots for {product} are almost full', 'Book your {product} demo before it is too late', 'Last few demo spots this week for {product}'],
      adCopy: ['Demo spots for {product} are limited this week. Book now and see {value} live.', 'Do not miss your chance to see {product} in action. Book a demo today. Slots are filling up.'],
    },
    Playful: {
      buttons: ['Show Me the Magic', 'Ooh, I Want a Demo', 'Let\'s See {product}!', 'Demo Time!', 'Sign Me Up for a Demo'],
      headlines: ['{product} Demo: Prepare to Be Impressed', 'Book a Demo and See Why {product} Fans Are Obsessed', 'Your {product} Demo Is Going to Be Fun (Seriously)'],
      emailSubjects: ['Demo time! Let us show you {product}', '{product} demo = mind blown', 'Ready to see something cool? Book a {product} demo.'],
      adCopy: ['{product} demos are surprisingly fun. Book one and see how {value} actually works in real life.', 'Spoiler: your {product} demo is going to be the best 15 minutes of your week. Book it.'],
    },
    Bold: {
      buttons: ['Book a Demo Now', 'See the Proof - Book Demo', 'Get Your Demo', 'Demand a Demo', 'Schedule and See Results'],
      headlines: ['Stop Guessing. Book a {product} Demo and See {value}.', '{product} Speaks for Itself. Book a Demo and Watch.', 'The {industry} Demo You Cannot Afford to Skip'],
      emailSubjects: ['See {product} prove {value}. Book a demo.', 'The demo that changes everything for {industry}', '{product}: results that speak louder than words'],
      adCopy: ['{product} does not need hype. Book a demo and watch it deliver {value} live. Results speak for themselves.', 'The best {industry} decision you will make this quarter: booking a {product} demo. Do it now.'],
    },
  },
  'Subscribe': {
    Professional: {
      buttons: ['Subscribe Now', 'Join the List', 'Subscribe to {product}', 'Get Updates', 'Stay Informed'],
      headlines: ['Subscribe to {product} for Exclusive {value} Insights', 'Stay Ahead in {industry} - Subscribe to {product}', 'Get {value} Tips Delivered to Your Inbox'],
      emailSubjects: ['Subscribe to {product} for weekly {value} tips', 'Stay ahead: subscribe to {product}', '{industry} insights from {product} - subscribe now'],
      adCopy: ['Subscribe to {product} and get {value} insights delivered straight to your inbox. Free for {industry} professionals.', 'Join the {product} newsletter. {value} tips, industry trends, and actionable advice. Subscribe free.'],
    },
    Casual: {
      buttons: ['Join In', 'Sign Me Up', 'I Want In', 'Subscribe Free', 'Keep Me Posted'],
      headlines: ['Get {value} Tips from {product} - No Spam, Promise', 'Subscribe to {product} - It Is Actually Worth Reading', '{product} Newsletter: Quick Reads, Big {value}'],
      emailSubjects: ['Welcome aboard - {product} newsletter', '{product} updates you will actually enjoy', 'Subscribe to {product} - no spam, ever'],
      adCopy: ['{product} emails are short, useful, and spam-free. Subscribe and get {value} tips every week.', 'Join the {product} newsletter. It is the one email about {value} you will actually look forward to.'],
    },
    Urgent: {
      buttons: ['Subscribe Now - Do Not Miss Out', 'Join Before It Is Full', 'Subscribe Today', 'Get Access Now', 'Limited Spots - Subscribe'],
      headlines: ['Subscribe to {product} Before You Miss the Next {value} Update', 'Your {industry} Competitors Are Already Subscribed to {product}', 'Do Not Fall Behind: Subscribe to {product} Now'],
      emailSubjects: ['Subscribers are getting {value}. Are you?', 'Subscribe to {product} before you miss out', 'Last call: join the {product} list'],
      adCopy: ['Subscribe to {product} now. Your competitors in {industry} already have. Do not miss the {value} insights.', '{product} subscribers get {value} tips first. Subscribe before the next issue drops.'],
    },
    Playful: {
      buttons: ['Count Me In!', 'Subscribe - Let\'s Go', 'Yes, I Want {value}', 'Hit Me With Updates', 'Join the Cool Kids'],
      headlines: ['{product} Newsletter: Where {value} Meets Your Inbox', 'Subscribe and Let {product} Do the Heavy Lifting', 'Join the {product} Crew - Free {value} Every Week'],
      emailSubjects: ['You are going to love the {product} newsletter', '{product} + your inbox = {value}', 'Join the crew: subscribe to {product}'],
      adCopy: ['{product} newsletter: short, sweet, and packed with {value}. Subscribe and enjoy.', 'Your inbox deserves better. Subscribe to {product} for {value} content you will actually read.'],
    },
    Bold: {
      buttons: ['Subscribe and Lead', 'Get the Edge - Subscribe', 'Join {product} Now', 'Subscribe to Win', 'Unlock {value}'],
      headlines: ['Leaders in {industry} Subscribe to {product}. Do You?', 'Subscribe to {product}: The Fastest Path to {value}', 'Stop Falling Behind. Subscribe to {product} and Own {value}.'],
      emailSubjects: ['{industry} leaders subscribe to {product}. Your turn.', 'Subscribe to {product} and dominate {value}', 'The {value} edge: {product} newsletter'],
      adCopy: ['The smartest {industry} professionals subscribe to {product}. Join them and get {value} delivered weekly.', '{product} subscribers do not just stay informed. They stay ahead. Subscribe now and {value}.'],
    },
  },
  'Get Quote': {
    Professional: {
      buttons: ['Get a Quote', 'Request Your Quote', 'Get Pricing', 'See Custom Pricing', 'Request a Proposal'],
      headlines: ['Get a Custom {product} Quote for Your {industry} Business', 'See What {product} Costs for Your Specific Needs', 'Request a Quote and Discover How {product} Delivers {value}'],
      emailSubjects: ['Your custom {product} quote is ready', 'Get pricing for {product} today', '{product} quote request - {value} for {industry}'],
      adCopy: ['Get a custom {product} quote tailored to your {industry} needs. See how it delivers {value}.', 'Request a {product} quote and discover transparent pricing for {value}. No obligation.'],
    },
    Casual: {
      buttons: ['See Pricing', 'Get My Quote', 'How Much?', 'Check the Price', 'What Will It Cost?'],
      headlines: ['Wondering What {product} Costs? Get a Quick Quote', 'Get Your {product} Price in Minutes', '{product} Pricing - Transparent, Fair, No Surprises'],
      emailSubjects: ['Curious about {product} pricing?', 'Get your {product} quote in 2 minutes', '{product} pricing made simple'],
      adCopy: ['Curious what {product} costs? Get a quick quote. No pushy sales calls, just real numbers.', '{product} pricing is transparent and fair. Request a quote and see for yourself.'],
    },
    Urgent: {
      buttons: ['Get Your Quote Now', 'Pricing Goes Up Soon - Get Quote', 'Request Quote Today', 'Lock In Your Price', 'Quote Before Price Increase'],
      headlines: ['Get Your {product} Quote Before Prices Increase', 'Lock In Today\'s {product} Pricing - Request a Quote Now', 'Pricing Is Changing Soon. Get Your {product} Quote Today.'],
      emailSubjects: ['Prices going up: get your {product} quote now', 'Lock in {product} pricing today', 'Last chance for current {product} rates'],
      adCopy: ['{product} pricing is increasing soon. Get your quote now and lock in today\'s rate for {value}.', 'Do not wait for the price increase. Request your {product} quote today and save.'],
    },
    Playful: {
      buttons: ['Show Me the Price', 'What\'s the Damage?', 'Get My Number', 'Let\'s Talk Pricing', 'Surprise Me'],
      headlines: ['{product} Pricing: No Sticker Shock, We Promise', 'Get a Quote for {product} - It Is Less Than You Think', 'What Does {product} Cost? Let Us Show You.'],
      emailSubjects: ['Let us talk {product} pricing', '{product} costs less than you think', 'Your {product} quote (no surprises inside)'],
      adCopy: ['{product} pricing is friendlier than you expect. Get a quote and see for yourself.', 'Get a {product} quote. Spoiler: it is probably less than what you are spending now to {value}.'],
    },
    Bold: {
      buttons: ['Get Your Quote', 'See the ROI - Get Quote', 'Demand Pricing', 'Get Real Numbers', 'Know Your Investment'],
      headlines: ['Know Exactly What {product} Costs. Get a Quote Now.', '{product} Pricing Is Transparent. Request a Quote and See.', 'Your {industry} ROI Starts with a {product} Quote'],
      emailSubjects: ['Get real {product} numbers', '{product}: pricing that makes sense for {industry}', 'Your {product} investment, your terms'],
      adCopy: ['{product} pays for itself. Get a quote and see the ROI math for your {industry} business.', 'Request a {product} quote. Real numbers, no games, no hidden fees. Just {value}.'],
    },
  },
};

function interpolate(template: string, product: string, value: string, industry: string): string {
  return template
    .replace(/\{product\}/g, product)
    .replace(/\{value\}/g, value)
    .replace(/\{industry\}/g, industry);
}

const TOOL_ICONS: Record<string, React.ReactNode> = {
  default: <HiOutlineMail />,
};

function getWidgetDescription(slug: string): string {
  switch (slug) {
    case 'popup-builder':
      return 'Turn your best CTA copy into high-converting popups. Exit-intent, timed, and scroll-triggered popups capture visitors at the perfect moment.';
    case 'countdown':
      return 'Add urgency to any CTA with a countdown timer. Deadlines drive action and boost conversion rates on landing pages, emails, and offers.';
    case 'testimonials':
      return 'Pair your CTAs with social proof. Displaying real testimonials next to a call to action builds trust and increases click-through rates.';
    case 'spinning-wheel':
      return 'Gamify your CTAs with a spin-the-wheel popup. Visitors engage with the game, enter their email, and convert at higher rates than static forms.';
    default:
      return 'Boost your CTA performance with the right on-site engagement tools.';
  }
}

function getWidgetCTA(slug: string): string {
  switch (slug) {
    case 'popup-builder':
      return 'Add Popups Free →';
    case 'countdown':
      return 'Add Countdown Free →';
    case 'testimonials':
      return 'Add Testimonials Free →';
    case 'spinning-wheel':
      return 'Add Spin the Wheel Free →';
    default:
      return 'Try It Free →';
  }
}

export const CTACopyTool = ({ recommendedWidgets, relatedTools }: IProps) => {
  const [productName, setProductName] = useState('');
  const [valueProp, setValueProp] = useState('');
  const [action, setAction] = useState<TAction>('Start Free Trial');
  const [tone, setTone] = useState<TTone>('Professional');
  const [industry, setIndustry] = useState('SaaS');
  const [result, setResult] = useState<ICTAResult | null>(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [error, setError] = useState('');
  const [copiedIndex, setCopiedIndex] = useState<string | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const handleGenerate = () => {
    if (!productName.trim()) {
      setError('Please enter your product or service name.');
      return;
    }
    if (!valueProp.trim()) {
      setError('Please enter your value proposition.');
      return;
    }

    setError('');

    const templates = CTA_TEMPLATES[action][tone];
    const buttons = templates.buttons.map((t) => interpolate(t, productName.trim(), valueProp.trim(), industry));
    const headlines = templates.headlines.map((t) => interpolate(t, productName.trim(), valueProp.trim(), industry));
    const emailSubjects = templates.emailSubjects.map((t) => interpolate(t, productName.trim(), valueProp.trim(), industry));
    const adCopy = templates.adCopy.map((t) => interpolate(t, productName.trim(), valueProp.trim(), industry));

    setResult({
      buttons,
      headlines,
      emailSubjects,
      adCopy,
      productName: productName.trim(),
      status: 'good',
    });
    setHasCalculated(true);
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(id);
    setTimeout(() => setCopiedIndex(null), 1500);
  };

  return (
    <>
      <StyledToolHero>
        <h1>Free <span className="highlight">CTA Copy</span> Generator</h1>
        <p>
          Generate high-converting CTA copy for buttons, headlines, emails, and ads.
          Tailored to your product, tone, and industry.
        </p>
      </StyledToolHero>

      <StyledToolBody>
        <StyledCalculator>
          <h2>Generate Your CTA Copy</h2>

          <StyledFieldsGrid>
            <StyledFormGroup>
              <label htmlFor="productName">Product / Service Name</label>
              <input
                id="productName"
                type="text"
                placeholder="e.g. CommonNinja"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
              />
              <p className="hint">The name of your product, service, or brand</p>
            </StyledFormGroup>

            <StyledFormGroup>
              <label htmlFor="industry">Industry</label>
              <select
                id="industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
              >
                <option value="SaaS">SaaS</option>
                <option value="E-Commerce">E-Commerce</option>
                <option value="Agency">Agency</option>
                <option value="Education">Education</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Finance">Finance</option>
              </select>
              <p className="hint">Your business industry for tailored messaging</p>
            </StyledFormGroup>

            <StyledFullWidth>
              <StyledFormGroup>
                <label htmlFor="valueProp">Value Proposition</label>
                <textarea
                  id="valueProp"
                  placeholder="e.g. grow your email list 3x faster"
                  value={valueProp}
                  onChange={(e) => setValueProp(e.target.value)}
                />
                <p className="hint">A short description of the main benefit you deliver</p>
              </StyledFormGroup>
            </StyledFullWidth>

            <StyledFormGroup>
              <label htmlFor="action">Target Action</label>
              <select
                id="action"
                value={action}
                onChange={(e) => setAction(e.target.value as TAction)}
              >
                <option value="Sign Up">Sign Up</option>
                <option value="Buy Now">Buy Now</option>
                <option value="Learn More">Learn More</option>
                <option value="Download">Download</option>
                <option value="Start Free Trial">Start Free Trial</option>
                <option value="Book Demo">Book Demo</option>
                <option value="Subscribe">Subscribe</option>
                <option value="Get Quote">Get Quote</option>
              </select>
              <p className="hint">The primary action you want visitors to take</p>
            </StyledFormGroup>

            <StyledFormGroup>
              <label htmlFor="tone">Tone</label>
              <select
                id="tone"
                value={tone}
                onChange={(e) => setTone(e.target.value as TTone)}
              >
                <option value="Professional">Professional</option>
                <option value="Casual">Casual</option>
                <option value="Urgent">Urgent</option>
                <option value="Playful">Playful</option>
                <option value="Bold">Bold</option>
              </select>
              <p className="hint">The voice and energy of your CTA copy</p>
            </StyledFormGroup>
          </StyledFieldsGrid>

          {error && (
            <p style={{ color: '#e22029', fontSize: '0.875rem', marginBottom: '16px' }}>{error}</p>
          )}

          <StyledCalculateButton onClick={handleGenerate}>
            Generate My CTAs
          </StyledCalculateButton>
        </StyledCalculator>

        {hasCalculated && result && (
          <>
            <StyledResult status={result.status} ref={resultRef}>
              <p className="result-label">Your Generated CTAs</p>
              <p className="result-number">13 Variations</p>
              <div className="result-badge">
                <span>Ready to Use</span>
              </div>

              <StyledCTACategory>
                <h3>Button Text (5 options)</h3>
                {result.buttons.map((cta, i) => (
                  <StyledCTAOption key={`btn-${i}`}>
                    <span className="cta-text">{cta}</span>
                    <button
                      className="copy-btn"
                      onClick={() => handleCopy(cta, `btn-${i}`)}
                    >
                      {copiedIndex === `btn-${i}` ? 'Copied!' : 'Copy'}
                    </button>
                  </StyledCTAOption>
                ))}
              </StyledCTACategory>

              <StyledCTACategory>
                <h3>Headlines (3 options)</h3>
                {result.headlines.map((cta, i) => (
                  <StyledCTAOption key={`hl-${i}`}>
                    <span className="cta-text">{cta}</span>
                    <button
                      className="copy-btn"
                      onClick={() => handleCopy(cta, `hl-${i}`)}
                    >
                      {copiedIndex === `hl-${i}` ? 'Copied!' : 'Copy'}
                    </button>
                  </StyledCTAOption>
                ))}
              </StyledCTACategory>

              <StyledCTACategory>
                <h3>Email Subject Lines (3 options)</h3>
                {result.emailSubjects.map((cta, i) => (
                  <StyledCTAOption key={`em-${i}`}>
                    <span className="cta-text">{cta}</span>
                    <button
                      className="copy-btn"
                      onClick={() => handleCopy(cta, `em-${i}`)}
                    >
                      {copiedIndex === `em-${i}` ? 'Copied!' : 'Copy'}
                    </button>
                  </StyledCTAOption>
                ))}
              </StyledCTACategory>

              <StyledCTACategory>
                <h3>Ad Copy (2 options)</h3>
                {result.adCopy.map((cta, i) => (
                  <StyledCTAOption key={`ad-${i}`}>
                    <span className="cta-text">{cta}</span>
                    <button
                      className="copy-btn"
                      onClick={() => handleCopy(cta, `ad-${i}`)}
                    >
                      {copiedIndex === `ad-${i}` ? 'Copied!' : 'Copy'}
                    </button>
                  </StyledCTAOption>
                ))}
              </StyledCTACategory>
            </StyledResult>

            <StyledWidgetsSection>
              <h2>Turn Your CTAs into Conversions</h2>
              <p className="subtitle">
                These widgets help thousands of businesses turn great CTA copy into real leads and sales.
              </p>
              <StyledWidgetGrid>
                {recommendedWidgets.map((widget) => (
                  <StyledWidgetCard
                    key={widget._id}
                    href={`/widgets/${widget.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="widget-header">
                      {widget.logo && (
                        <Image
                          src={widget.logo}
                          alt={`${widget.name} logo`}
                          width={48}
                          height={48}
                        />
                      )}
                      <h3>{widget.name}</h3>
                    </div>
                    <p>{getWidgetDescription(widget.slug)}</p>
                    <span className="widget-cta">{getWidgetCTA(widget.slug)}</span>
                  </StyledWidgetCard>
                ))}
              </StyledWidgetGrid>
            </StyledWidgetsSection>
          </>
        )}

        {relatedTools.length > 0 && (
          <StyledRelatedTools>
            <h2>Explore More Free Tools</h2>
            <p className="subtitle">
              Other free tools to help you generate and convert more leads.
            </p>
            <StyledRelatedToolGrid>
              {relatedTools.map((tool) => (
                <StyledRelatedToolCard
                  key={tool.slug}
                  as={Link}
                  href={`/free-tools/lead-generation/${tool.slug}`}
                >
                  <span className="tool-icon">
                    {TOOL_ICONS[tool.slug] || <HiOutlineTrendingUp />}
                  </span>
                  <h3>{tool.name}</h3>
                  <p>{tool.description}</p>
                  <span className="tool-cta">Use Tool →</span>
                </StyledRelatedToolCard>
              ))}
            </StyledRelatedToolGrid>
          </StyledRelatedTools>
        )}

      </StyledToolBody>
    </>
  );
};
